import { OptionI } from '@/utils/forms/optionBuilders'

/**
 * Checks if an option label matches a search term (case-insensitive).
 *
 * @param option - Option to check
 * @param searchTerm - Search term to match against
 * @returns True if the option label contains the search term
 */
export function isOptionMatchingSearch(
  option: OptionI,
  searchTerm: string
): boolean {
  const optionLabel = option.label ?? ''
  const searchTermLower = searchTerm.toLowerCase()
  return optionLabel.toLowerCase().includes(searchTermLower)
}

/**
 * Creates a function to efficiently check if an option is selected.
 *
 * @param selectedOptions - Array of currently selected options
 * @returns Function that checks if a given option is selected
 */
export function createIsSelectedChecker(selectedOptions?: OptionI[]) {
  const selectedValuesSet = new Set(
    selectedOptions?.map((option) => option.value) ?? []
  )
  return (option: OptionI) => selectedValuesSet.has(option.value)
}

/**
 * Removes duplicate options based on their values.
 *
 * @param options - Array of options to deduplicate
 * @returns Array of unique options
 */
export function removeDuplicateOptions(options: OptionI[]): OptionI[] {
  const seen = new Set()
  return options.filter((option) => {
    if (seen.has(option.value)) {
      return false
    }
    seen.add(option.value)
    return true
  })
}

/**
 * Sorts options with selected items first, then alphabetically by label.
 *
 * @param options - Array of options to sort
 * @param selectedOptions - Array of currently selected options
 * @returns Sorted array with selected options first, then alphabetical
 */
export function getSortedOptions(
  options: OptionI[],
  selectedOptions: OptionI[]
) {
  const isSelected = createIsSelectedChecker(selectedOptions)
  return [...options].sort((a, b) => {
    const isASelected = isSelected(a)
    const isBSelected = isSelected(b)

    if (isASelected === isBSelected) {
      // Sort alphabetically by label
      const labelA = a.label ?? ''
      const labelB = b.label ?? ''
      return labelA.localeCompare(labelB)
    }

    // Sort selected options first
    return isASelected ? -1 : 1
  })
}

/**
 * Filters options based on search mode and search term.
 * For external search, returns all options as filtering is handled externally.
 * For internal search, filters options based on the search term.
 *
 * @param options - Array of options to filter
 * @param searchTerm - Current search term
 * @param isExternalSearch - Whether search is handled externally
 * @returns Filtered array of options
 */
export function filterOptions(
  options: OptionI[] | undefined,
  searchTerm: string,
  isExternalSearch: boolean
): OptionI[] {
  if (!options) {
    return []
  }

  if (isExternalSearch) {
    return [...options]
  }

  return options.filter((option) => isOptionMatchingSearch(option, searchTerm))
}

/**
 * Checks if there are any unselected options in the provided options array.
 *
 * @param options - Array of available options to check
 * @param selectedOptions - Array of currently selected options
 * @returns True if there are unselected options, false otherwise
 */
export function hasUnselectedOptions(
  options: OptionI[],
  selectedOptions: OptionI[]
): boolean {
  const selectedOptionsSet = new Set(
    (selectedOptions ?? []).map((option) => option.value)
  )

  return options.some((option) => !selectedOptionsSet.has(option.value))
}
